/**
 * Shared utility functions for logger modules
 */

import {
  LOG_LEVEL_PRIORITY,
  type LogLevel,
  MAX_NAMESPACE_WIDTH,
} from '@maestro/core/logger/constants'

/**
 * Truncate namespace from the beginning if too long.
 * Long namespaces are truncated with "..." prefix.
 *
 * @param ns - The namespace string
 * @param maxWidth - Maximum width (defaults to MAX_NAMESPACE_WIDTH)
 * @returns Truncated and right-aligned namespace string
 */
export function truncateNamespace(
  ns: string,
  maxWidth: number = MAX_NAMESPACE_WIDTH
): string {
  if (ns.length <= maxWidth) {
    return ns.padStart(maxWidth)
  }
  return `...${ns.slice(-(maxWidth - 3))}`
}

/**
 * Format current time as HH:MM:SS for log output
 *
 * @returns Time string in HH:MM:SS format
 */
export function formatTimestamp(): string {
  const now = new Date()
  return [
    now.getHours().toString().padStart(2, '0'),
    now.getMinutes().toString().padStart(2, '0'),
    now.getSeconds().toString().padStart(2, '0'),
  ].join(':')
}

/**
 * Check if a message should be logged based on level priority.
 * Lower priority numbers mean more important/severe levels.
 *
 * @param messageLevel - The level of the message being logged
 * @param configuredLevel - The configured threshold level
 * @returns true if the message should be logged
 */
export function shouldLog(
  messageLevel: LogLevel,
  configuredLevel: LogLevel
): boolean {
  return LOG_LEVEL_PRIORITY[messageLevel] <= LOG_LEVEL_PRIORITY[configuredLevel]
}

/**
 * Get namespace width from environment variable or use default.
 * Configurable via LOG_NAMESPACE_WIDTH env var (range: 10-100).
 *
 * @returns Configured namespace width
 */
export function getNamespaceWidth(): number {
  if (typeof process !== 'undefined' && process.env?.LOG_NAMESPACE_WIDTH) {
    const width = parseInt(process.env.LOG_NAMESPACE_WIDTH, 10)
    if (!isNaN(width) && width >= 10 && width <= 100) {
      return width
    }
  }
  return MAX_NAMESPACE_WIDTH
}

/**
 * JSON.stringify that handles circular references instead of throwing.
 * Only true cycles (ancestor on the current path) are replaced with "[Circular]".
 * Shared references (same object in multiple places) are serialized normally.
 */
export function safeStringify(obj: unknown): string {
  const stack: unknown[] = []
  return JSON.stringify(obj, function (_key, value) {
    if (stack.length > 0) {
      const thisPos = stack.indexOf(this)
      if (thisPos !== -1) {
        stack.length = thisPos + 1
      } else {
        stack.push(this)
      }
      if (value !== null && typeof value === 'object') {
        if (stack.indexOf(value) !== -1) {
          return '[Circular]'
        }
      }
    } else {
      stack.push(value)
    }
    return value
  })
}
